'use client';

import React from 'react';
import { IconTopRated, IconWorst } from '@repo/svgs';
import { Divider, ListCell, SectionContainer } from '@/components/ui';
import { ModalTrigger } from '@/components/ui/ModalTrigger';
import { useOwner } from '../../context/OwnerProvider';
import { isTopRated, titleCase } from '@repo/utils';
import { TitleContainer } from './TitleContainer';
import { RatingContainer } from './RatingContainer';
import dynamic from 'next/dynamic';

// Lazy load modal components
const TopRatedModal = dynamic(() =>
  import('./modals/TopRatedModal').then(mod => mod.TopRatedModal),
);

const WorstLandlordModal = dynamic(() =>
  import('./modals/WorstLandlordModal').then(mod => mod.WorstLandlordModal),
);

/**
 * Owner Info and Rating - Main container component
 * Composed of TitleContainer, RatingContainer, and TopRated ListCell
 */
export function OwnerInfoAndRating() {
  const { owner } = useOwner();
  const {
    name,
    roles,
    numBuildings,
    numUnits,
    averageRating,
    reviewCount,
    latestWorstLandlordsYear,
  } = owner;
  const ownerName = name || 'Owner';
  const isOwnerTopRated = isTopRated(averageRating, reviewCount);

  return (
    <SectionContainer className="md:pl-2">
      {/* Title Container - matches Figma 5979-185923 */}
      <TitleContainer
        name={titleCase(ownerName)}
        roles={roles || []}
        numBuildings={numBuildings || 0}
        numUnits={numUnits || 0}
      />

      <Divider variant="tertiary" />

      <RatingContainer averageRating={averageRating || 0} reviewCount={reviewCount || 0} />

      <Divider variant="tertiary" />

      {isOwnerTopRated && (
        <ModalTrigger
          label="Top rated owner or associate"
          content={<TopRatedModal />}
          urlKey="top-rated"
          maxWidth="md"
        >
          <ListCell
            leadingIcon={<IconTopRated className="w-6 h-6 icon-rating" />}
            title={'Top rated owner or associate'}
            subtitle={`Associated buildings are highly reviewed and rated 4+ stars by past and current renters.`}
          />
        </ModalTrigger>
      )}
      {latestWorstLandlordsYear && (
        <ModalTrigger
          label="Worst landlord"
          content={<WorstLandlordModal />}
          urlKey="worst-landlord"
          maxWidth="md"
        >
          <ListCell
            leadingIcon={<IconWorst className="w-6 h-6 icon-rating" />}
            title={`Worst landlord`}
            subtitle={`This owner or associate is ranked in the top 100 worst landlords in NYC.`}
          />
        </ModalTrigger>
      )}
    </SectionContainer>
  );
}
